import axios from "./_axios";
import { API } from "./apiEndPoints";
import { buildFetchConfig, processSSEStream } from "./askIQApi";
import configService from "@/services/config";

function serializeJobFamilyListParams(params) {
  const parts = [];
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (Array.isArray(value)) {
      value.forEach((entry) => {
        if (entry !== undefined && entry !== "") {
          parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(entry)}`);
        }
      });
    } else if (value !== undefined && value !== "") {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    }
  });
  return parts.join("&");
}

function jobFamilyCollectionUrl(accountId) {
  return `${API.ACCOUNT_LIST_V2}${accountId}/${API.JOB_FAMILY}`;
}

function jobFamilyDetailUrl(accountId, familyId) {
  return `${jobFamilyCollectionUrl(accountId)}${familyId}/`;
}

function jobFamilyActionUrl(accountId, familyId, action) {
  return `${jobFamilyDetailUrl(accountId, familyId)}${action}/`;
}

function jobFamilyLevelCollectionUrl(accountId, familyId) {
  return `${jobFamilyDetailUrl(accountId, familyId)}levels/`;
}

function jobFamilyLevelDetailUrl(accountId, familyId, levelId) {
  return `${jobFamilyLevelCollectionUrl(accountId, familyId)}${levelId}/`;
}

function jobFamilyLevelReorderUrl(accountId, familyId) {
  return `${jobFamilyLevelCollectionUrl(accountId, familyId)}reorder/`;
}

function jobFamilyLevelSuggestUrl(accountId, familyId) {
  return `${jobFamilyLevelCollectionUrl(accountId, familyId)}suggest/`;
}

function jobFamilyLevelSuggestUndoUrl(accountId, familyId) {
  return `${jobFamilyLevelCollectionUrl(accountId, familyId)}suggest/undo/`;
}

/**
 * GET /api/common/v2/account/<id>/job-family/
 * Paginated list. Supports `search` (name/code) and repeated `status` params
 * (`status=draft&status=active`). ER-11648: has_levels and has_positions params
 * removed — they remain in the API for compatibility but are no longer sent.
 */
export const getListOfJobFamilies = (accountId, filters = {}, pagination = {}) => {
  const params = { ordering: filters.ordering || "name" };
  const trimmedSearch = typeof filters.search === "string" ? filters.search.trim() : "";
  if (trimmedSearch) {
    params.search = trimmedSearch;
  }
  if (filters.status && filters.status.length > 0) {
    params.status = filters.status;
  }
  if (pagination.page != null) {
    params.page = pagination.page;
  }
  if (pagination.pageSize != null) {
    params.page_size = pagination.pageSize;
  }
  return axios()({
    method: "get",
    url: jobFamilyCollectionUrl(accountId),
    params,
    paramsSerializer: serializeJobFamilyListParams,
  });
};

export const getJobFamily = (accountId, familyId) => {
  return axios()({
    method: "get",
    url: jobFamilyDetailUrl(accountId, familyId),
  });
};

/**
 * GET /api/common/v2/account/<id>/job-family/<familyId>/levels/
 * Unpaginated list of a Family's Levels, ordered by ordinal ascending (rules §3.2).
 * Each row is `{ id, ordinal, name, descriptor, position_count }`. The caller is
 * responsible for the descending ladder-order render (the picker sorts it).
 */
export const getListOfJobFamilyLevels = (accountId, familyId) => {
  return axios()({
    method: "get",
    url: `${jobFamilyDetailUrl(accountId, familyId)}levels/`,
  });
};

export const createJobFamily = (accountId, data) => {
  return axios()({
    method: "post",
    url: jobFamilyCollectionUrl(accountId),
    data,
  });
};

/**
 * PATCH /api/common/v2/account/<id>/job-family/<familyId>/
 * Generic entity partial update. The Context tab saves its value through this
 * endpoint as `{ context }` (same shape as the role/position hosts), so the
 * context field must be updatable on the existing Job Family update path.
 */
export const updateJobFamily = (accountId, familyId, data) => {
  return axios()({
    method: "patch",
    url: jobFamilyDetailUrl(accountId, familyId),
    data,
  });
};

export const deleteJobFamily = (accountId, familyId) => {
  return axios()({
    method: "delete",
    url: jobFamilyDetailUrl(accountId, familyId),
  });
};

/** Lifecycle transitions — POST .../job-family/{id}/<action>/ (MDP-581). */
function jobFamilyLifecycleAction(accountId, familyId, action, data) {
  return axios()({
    method: "post",
    url: jobFamilyActionUrl(accountId, familyId, action),
    ...(data ? { data } : {}),
  });
}

export const activateJobFamily = (accountId, familyId) =>
  jobFamilyLifecycleAction(accountId, familyId, "activate");

export const retireJobFamily = (accountId, familyId) =>
  jobFamilyLifecycleAction(accountId, familyId, "retire");

export const reactivateJobFamily = (accountId, familyId) =>
  jobFamilyLifecycleAction(accountId, familyId, "reactivate");

export const archiveJobFamily = (accountId, familyId) =>
  jobFamilyLifecycleAction(accountId, familyId, "archive");

/** `name` (ER-11892) renames the family atomically with the unarchive. */
export const unarchiveJobFamily = (accountId, familyId, name) =>
  jobFamilyLifecycleAction(accountId, familyId, "unarchive", name ? { name } : undefined);

/**
 * GET /api/common/v2/account/<id>/job-family/<familyId>/levels/
 * List a family's levels, ordinal ascending (Level 1 first). No pagination.
 */
export const getJobFamilyLevels = (accountId, familyId) => {
  return axios()({
    method: "get",
    url: jobFamilyLevelCollectionUrl(accountId, familyId),
  });
};

/**
 * POST /api/common/v2/account/<id>/job-family/<familyId>/levels/
 * Create a level on top of the family's ladder. Payload `{ name, descriptor }`.
 */
export const createJobFamilyLevel = (accountId, familyId, data) => {
  return axios()({
    method: "post",
    url: jobFamilyLevelCollectionUrl(accountId, familyId),
    data,
  });
};

/**
 * PATCH /api/common/v2/account/<id>/job-family/<familyId>/levels/<levelId>/
 * Edit a level's name and/or descriptor. Ordinal is never authored here.
 */
export const updateJobFamilyLevel = (accountId, familyId, levelId, data) => {
  return axios()({
    method: "patch",
    url: jobFamilyLevelDetailUrl(accountId, familyId, levelId),
    data,
  });
};

/**
 * DELETE /api/common/v2/account/<id>/job-family/<familyId>/levels/<levelId>/
 * Delete a level, moving its live Positions when `reassignTo` is supplied. The
 * response carries the family's remaining levels in their new order.
 */
export const deleteJobFamilyLevel = (accountId, familyId, levelId, reassignTo) => {
  return axios()({
    method: "delete",
    url: jobFamilyLevelDetailUrl(accountId, familyId, levelId),
    data: { reassign_to: reassignTo },
  });
};

/**
 * PATCH /api/common/v2/account/<id>/job-family/<familyId>/levels/reorder/
 * Renumber the family to the given order. `orderedLevelIds` is the complete
 * ordinal-ascending (bottom-first) permutation of level ids.
 */
export const reorderJobFamilyLevels = (accountId, familyId, orderedLevelIds) => {
  return axios()({
    method: "patch",
    url: jobFamilyLevelReorderUrl(accountId, familyId),
    data: { level_ids: orderedLevelIds },
  });
};

/**
 * POST /api/common/v2/account/<id>/job-family/<familyId>/levels/suggest/
 * Orchestration endpoint (ER-11792): IQ suggests/extends the Family's Levels,
 * saving them immediately. Inputs (Job Family Name, Context, existing Levels and
 * Account IQ Context) are collected server-side, so this call carries no payload.
 * Returns `{ levels, snapshot_id, changes }` — the reconciled level list, the
 * session-scoped snapshot id for Undo, and the per-row `[{ level_id, kind }]`
 * flags ("new" / "updated") the table renders as badges.
 *
 * `signal` is an optional AbortSignal (ER-11833) forwarded to axios so the caller
 * can cancel the in-flight request from the "Abort" action.
 *
 * `snapshotId` is an optional pending snapshot id (ER-11826): when the caller still
 * holds an un-undone suggestion snapshot, Suggest chains onto it so the service
 * updates the saved levels in place instead of creating a fresh batch.
 */
export const suggestJobFamilyLevels = (accountId, familyId, signal, snapshotId) => {
  return axios()({
    method: "post",
    url: jobFamilyLevelSuggestUrl(accountId, familyId),
    ...(snapshotId ? { data: { snapshot_id: snapshotId } } : {}),
    ...(signal ? { signal } : {}),
  });
};

/**
 * POST /api/common/v2/account/<id>/job-family/<familyId>/levels/suggest/ as a
 * streamed request (ER-12294, Streamed AI Thinking Standard). The endpoint returns
 * `text/event-stream` carrying the standard lifecycle:
 * - `event: phase`     → distilled phase label (`onPhase`)
 * - `event: thinking`  → distilled narrative progress line (`onThinking`, accumulated)
 * - `event: stalled`   → silence watchdog (`onStalled`)
 * - `event: result`    → terminal `{ levels, changes, snapshot_id }`
 * - `event: error`     → terminal failure
 *
 * Until ER-12293 ships, the endpoint still answers with a plain (non-streamed) JSON
 * body; we detect that and resolve with the same payload so the surface keeps working
 * (Connecting → Streaming with the timer, no narrative). `signal` is the caller's
 * AbortController signal; `snapshotId` chains onto a pending, un-undone suggestion.
 */
export const suggestJobFamilyLevelsStream = async ({
  accountId,
  familyId,
  signal,
  snapshotId,
  onPhase,
  onThinking,
  onStalled,
}) => {
  // Native fetch ignores axios's defaults.baseURL, so resolve against the API host.
  const baseUrl = (configService.get("baseURL") || "").replace(/\/$/, "");
  const url = `${baseUrl}/${jobFamilyLevelSuggestUrl(accountId, familyId)}`;
  // Mirror the legacy axios call: no body when there is no pending snapshot.
  const payload = snapshotId ? { snapshot_id: snapshotId } : undefined;

  // The endpoint is a DRF view that answers 406 to an Accept of only text/event-stream while it
  // still returns JSON, so JSON stays acceptable until ER-12293 streams.
  const response = await fetch(
    url,
    buildFetchConfig(url, payload, signal, "text/event-stream, application/json;q=0.9")
  );

  if (!response.ok) {
    throw new Error(`Suggest job family levels failed (${response.status}).`);
  }

  const contentType = String(response.headers.get("content-type") || "").toLowerCase();

  // Legacy non-streamed response (ER-12293 not yet shipped).
  if (!contentType.includes("text/event-stream")) {
    const text = await response.text();
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch {
      throw new Error("Suggest job family levels returned an unreadable response.");
    }
    if (parsed && typeof parsed === "object" && parsed.error) {
      throw new Error(
        typeof parsed.error === "string" && parsed.error.trim()
          ? parsed.error.trim()
          : "Suggest job family levels failed."
      );
    }
    return { data: parsed };
  }

  const reader = response.body.getReader();
  const { finalResponse, streamError } = await processSSEStream(reader, {
    onPhase,
    onThinkingUpdate: onThinking,
    onStalled,
  });

  if (streamError) {
    throw new Error(streamError);
  }

  if (!finalResponse) {
    throw new Error("Suggest job family levels ended without a result.");
  }

  return { data: finalResponse };
};

/**
 * POST /api/common/v2/account/<id>/job-family/<familyId>/levels/suggest/undo/
 * Reverts the saved suggestions using the in-memory snapshot id returned by the
 * suggest call. Returns the restored `{ levels }` list.
 */
export const undoJobFamilyLevelSuggestion = (accountId, familyId, snapshotId) => {
  return axios()({
    method: "post",
    url: jobFamilyLevelSuggestUndoUrl(accountId, familyId),
    data: { snapshot_id: snapshotId },
  });
};
